import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, ExternalLink, FileText, Globe, Search } from 'lucide-react';
import { api } from '../../lib/api';
import RichTextEditor from '../../components/RichTextEditor';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const emptyForm = {
    title: '',
    body: '',
    show_in_footer: false,
    meta_title: '',
    meta_description: '',
    meta_image: '',
};

function slugify(text) {
    return (text || '').toLowerCase().trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export default function AdminPages() {
    const [pages, setPages] = useState([]);
    const [active, setActive] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [newPage, setNewPage] = useState({ title: '', slug: '' });
    const [deleteSlug, setDeleteSlug] = useState(null);
    const [creating, setCreating] = useState(false);
    const [pageSearch, setPageSearch] = useState('');

    const load = async () => {
        const r = await api.get('/admin/pages');
        setPages(r.data);
        if (!active && r.data[0]) selectPage(r.data[0].slug, r.data);
        else if (active) {
            const found = r.data.find((p) => p.slug === active);
            if (found) loadForm(found);
        }
    };

    const loadForm = (p) => {
        setForm({
            title: p.title || '',
            body: p.body || '',
            show_in_footer: !!p.show_in_footer,
            meta_title: p.meta_title || '',
            meta_description: p.meta_description || '',
            meta_image: p.meta_image || '',
        });
    };

    const selectPage = (slug, list) => {
        const p = (list || pages).find((x) => x.slug === slug);
        if (!p) return;
        setActive(slug);
        loadForm(p);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!active) return;
        setSaving(true);
        try {
            await api.put(`/admin/pages/${active}`, form);
            toast.success('Page saved.');
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const createPage = async () => {
        if (!newPage.title) { toast.error('Title required'); return; }
        const slug = slugify(newPage.slug || newPage.title);
        if (!slug) { toast.error('Invalid URL slug'); return; }
        setCreating(true);
        try {
            const r = await api.post('/admin/pages', { title: newPage.title, slug, body: '', show_in_footer: false });
            toast.success('Page created.');
            setShowNew(false);
            setNewPage({ title: '', slug: '' });
            await load();
            setActive(r.data.slug);
            loadForm(r.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Create failed');
        } finally {
            setCreating(false);
        }
    };

    const onDelete = async () => {
        if (!deleteSlug) return;
        try {
            await api.delete(`/admin/pages/${deleteSlug}`);
            toast.success('Page deleted.');
            setDeleteSlug(null);
            // Switch to first remaining
            const remaining = pages.filter((p) => p.slug !== deleteSlug);
            if (remaining[0]) {
                setActive(remaining[0].slug);
                loadForm(remaining[0]);
            } else {
                setActive(null);
                setForm(emptyForm);
            }
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Delete failed');
        }
    };

    const activePage = pages.find((p) => p.slug === active);
    const q = pageSearch.trim().toLowerCase();
    const matches = (p) => !q || (p.title || '').toLowerCase().includes(q) || (p.slug || '').toLowerCase().includes(q);
    const systemPages = pages.filter((p) => p.is_system && matches(p));
    const customPages = pages.filter((p) => !p.is_system && matches(p));

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setForm((f) => ({ ...f, meta_image: reader.result }));
        reader.readAsDataURL(file);
    };

    return (
        <div data-testid="admin-pages-page">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Atelier</p>
                    <h1 className="font-serif text-4xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>Pages</h1>
                </div>
                <button onClick={() => setShowNew(true)} data-testid="admin-pages-new-button" className="btn-burgundy px-5 py-2.5 rounded-xl text-xs tracking-[0.20em] uppercase inline-flex items-center gap-2">
                    <Plus className="w-4 h-4" /> New Page
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <aside className="lg:col-span-1 space-y-4">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--ink-muted)' }} />
                        <input
                            data-testid="admin-pages-search-input"
                            type="text"
                            placeholder="Search pages..."
                            value={pageSearch}
                            onChange={(e) => setPageSearch(e.target.value)}
                            className="input-luxury pl-9"
                        />
                    </div>
                    <div className="luxury-card p-4">
                        <p className="eyebrow mb-2" style={{ color: 'var(--ink-muted)' }}>System</p>
                        <ul className="space-y-1">
                            {systemPages.length === 0 && <li className="text-xs" style={{ color: 'var(--ink-muted)' }}>No matches.</li>}
                            {systemPages.map((p) => (
                                <li key={p.slug}>
                                    <button onClick={() => selectPage(p.slug)} data-testid={`admin-pages-select-${p.slug}`} className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors" style={{ backgroundColor: active === p.slug ? 'var(--brand-burgundy)' : 'transparent', color: active === p.slug ? '#fff' : 'var(--ink)' }}>
                                        <FileText className="w-3.5 h-3.5" /> {p.title}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="luxury-card p-4">
                        <div className="flex items-center justify-between mb-2">
                            <p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Custom</p>
                            <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{customPages.length}</span>
                        </div>
                        {customPages.length === 0 && <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>No custom pages yet.</p>}
                        <ul className="space-y-1">
                            {customPages.map((p) => (
                                <li key={p.slug} className="flex items-center gap-1">
                                    <button onClick={() => selectPage(p.slug)} data-testid={`admin-pages-select-${p.slug}`} className="flex-1 flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors" style={{ backgroundColor: active === p.slug ? 'var(--brand-burgundy)' : 'transparent', color: active === p.slug ? '#fff' : 'var(--ink)' }}>
                                        <Globe className="w-3.5 h-3.5" /> {p.title}
                                    </button>
                                    <button onClick={() => setDeleteSlug(p.slug)} className="p-1.5 hover:bg-[var(--brand-rose)]/50 rounded" aria-label="Delete page"><Trash2 className="w-3.5 h-3.5" style={{ color: 'var(--brand-burgundy)' }} /></button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </aside>

                <div className="lg:col-span-3">
                    {activePage ? (
                        <div className="luxury-card p-6 space-y-5">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <p className="eyebrow" style={{ color: 'var(--ink-muted)' }}>Editing</p>
                                    <h2 className="font-serif text-2xl mt-1" style={{ color: 'var(--brand-burgundy)' }}>{activePage.title}</h2>
                                    <a href={`/${activePage.slug === 'home' ? '' : activePage.slug}`} target="_blank" rel="noreferrer" className="eyebrow inline-flex items-center gap-1 mt-1" style={{ color: 'var(--brand-burgundy)' }}>
                                        <ExternalLink className="w-3 h-3" /> /{activePage.slug}
                                    </a>
                                </div>
                                <button onClick={save} disabled={saving} data-testid="admin-page-save-button" className="btn-burgundy px-5 py-2.5 rounded-xl text-xs uppercase tracking-[0.20em] disabled:opacity-60">{saving ? 'Saving...' : 'Save Page'}</button>
                            </div>

                            <div>
                                <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Title</label>
                                <input className="input-luxury" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-page-title-input" />
                            </div>

                            <div>
                                <label className="eyebrow block mb-2" style={{ color: 'var(--ink-muted)' }}>Body</label>
                                <RichTextEditor
                                    value={form.body}
                                    onChange={(v) => setForm({ ...form, body: v })}
                                    placeholder="Tell the story of this page..."
                                    minHeight={320}
                                    testId="admin-page-body-editor"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="inline-flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={form.show_in_footer} onChange={(e) => setForm({ ...form, show_in_footer: e.target.checked })} data-testid="admin-page-footer-toggle" />
                                    Show in footer links
                                </label>
                            </div>

                            <div className="border-t border-[rgba(90,36,50,0.10)] pt-5">
                                <p className="eyebrow mb-3" style={{ color: 'var(--brand-burgundy)' }}>SEO</p>
                                <div className="space-y-3">
                                    <div>
                                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Meta Title</label>
                                        <input className="input-luxury" placeholder={`${form.title} | by anfal`} value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} data-testid="admin-page-meta-title" />
                                        <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>Leave empty to use the page title. Recommended: 50-60 characters.</p>
                                    </div>
                                    <div>
                                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Meta Description</label>
                                        <textarea rows={2} className="input-luxury resize-none" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} data-testid="admin-page-meta-description" />
                                        <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>Recommended: 150-160 characters.</p>
                                    </div>
                                    <div>
                                        <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Meta Image (Open Graph)</label>
                                        {form.meta_image && <div className="mb-2 aspect-[16/9] rounded-lg overflow-hidden max-w-md"><img src={form.meta_image} alt="meta" className="w-full h-full object-cover" /></div>}
                                        <div className="flex gap-2 flex-wrap">
                                            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[rgba(90,36,50,0.18)] text-xs cursor-pointer hover:bg-[var(--brand-rose)]/30">
                                                Upload<input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                                            </label>
                                            <input className="input-luxury flex-1 min-w-[200px]" placeholder="Or paste URL" value={form.meta_image} onChange={(e) => setForm({ ...form, meta_image: e.target.value })} />
                                            {form.meta_image && <button type="button" onClick={() => setForm({ ...form, meta_image: '' })} className="px-3 py-2 rounded-lg border border-[rgba(90,36,50,0.18)] text-xs">Clear</button>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="luxury-card p-10 text-center text-sm" style={{ color: 'var(--ink-muted)' }}>
                            Select a page on the left, or create a new one.
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={showNew} onOpenChange={setShowNew}>
                <DialogContent className="bg-white max-w-md" data-testid="admin-pages-new-dialog">
                    <DialogHeader><DialogTitle className="font-serif text-2xl" style={{ color: 'var(--brand-burgundy)' }}>New Page</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>Title</label>
                            <input className="input-luxury" value={newPage.title} onChange={(e) => setNewPage({ ...newPage, title: e.target.value, slug: newPage.slug || slugify(e.target.value) })} data-testid="admin-new-page-title" />
                        </div>
                        <div>
                            <label className="eyebrow block mb-1" style={{ color: 'var(--ink-muted)' }}>URL Slug</label>
                            <div className="flex items-center gap-2">
                                <span className="text-sm" style={{ color: 'var(--ink-muted)' }}>byanfal.com/</span>
                                <input className="input-luxury flex-1" placeholder="e.g. sustainability" value={newPage.slug} onChange={(e) => setNewPage({ ...newPage, slug: slugify(e.target.value) })} data-testid="admin-new-page-slug" />
                            </div>
                            <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>Auto-generated from title. Use letters, numbers and hyphens only.</p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <button onClick={() => setShowNew(false)} className="px-4 py-2 text-xs uppercase tracking-[0.18em]">Cancel</button>
                        <button onClick={createPage} disabled={creating} data-testid="admin-new-page-create" className="btn-burgundy px-5 py-2.5 rounded-xl text-xs uppercase tracking-[0.18em] disabled:opacity-60">{creating ? 'Creating...' : 'Create Page'}</button>
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteSlug} onOpenChange={(v) => !v && setDeleteSlug(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete this page?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the page and its URL.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={onDelete} className="btn-burgundy">Delete</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
